<template>
  <div class="admin-view">
    <page-header title="Admin" />

    <section>
      <h3>Users</h3>

      <div class="search-container">
        <form class="search-form" @submit.prevent="handleSearch">
          <align small-gap>
            <text-input
              v-model="searchQuery"
              label="Search Users"
              type="search"
              placeholder="Search by email or user ID..."
            />

            <btn
              type="submit"
              :loading="searching"
              :loading-text="searching ? 'Searching...' : 'Search'"
              :disabled="!searchQuery.trim()"
            >
              Search
            </btn>
          </align>
        </form>
      </div>

      <div class="create-container">
        <form class="create-form" @submit.prevent="handleCreate">
          <align small-gap>
            <text-input
              v-model="emailInput"
              label="Email"
              type="email"
              placeholder="Enter new user email"
            />

            <btn
              type="submit"
              :loading="creating"
              :loading-text="creating ? 'Creating...' : 'Create'"
              :disabled="!emailInput.trim()"
            >
              Create
            </btn>
          </align>
        </form>
      </div>
    </section>

    <form @submit.prevent="handleCreateFreeStripeAccount">
      <h3>Free Stripe Account</h3>

      <align class="free-account-input" flex>
        <div class="input-group">
          <text-input
            v-model="freeAccountEmail"
            label="Email"
            type="email"
            placeholder="Enter user email"
          />

          <text-input
            v-model="freeAccountCoupon"
            label="Stripe Coupon Code"
            placeholder="Enter Stripe coupon code"
          />
        </div>

        <btn
          type="submit"
          :loading="creatingFreeAccount"
          :loading-text="creatingFreeAccount ? 'Creating...' : 'Create Account'"
          :disabled="!freeAccountEmail.trim() || !freeAccountCoupon.trim()"
        >
          Create Account
        </btn>
      </align>
    </form>

    <form>
      <h3>Graded Cards</h3>

      <align class="cert-input" flex>
        <text-input
          v-model="certInput"
          label="Cert #"
          placeholder="63444200"
        />

        <select-input
          v-model="graderInput"
          label="Grader"
          :options="graderOptions"
        />

        <btn
          :loading="refreshingCert"
          loading-text="Submitting"
          :disabled="!certInput.trim() || !graderInput"
          @click="refreshCert"
        >
          Refresh Cert #
        </btn>
      </align>
    </form>
  </div>
</template>

<script>
import firebase from 'firebase/compat/app';

import { GRADING_COMPANY_UPLOAD_OPTIONS } from '@cardladder/shared/constants';

export default {
  data() {
    return {
      loaded: false,
      searchQuery: '',
      searching: false,
      emailInput: '',
      creating: false,
      freeAccountEmail: '',
      freeAccountCoupon: '',
      creatingFreeAccount: false,
      certInput: '',
      graderInput: 'psa',
      graderOptions: GRADING_COMPANY_UPLOAD_OPTIONS,
      refreshingCert: false,
    };
  },

  methods: {
    async handleSearch() {
      if (!this.searchQuery.trim()) {
        return;
      }

      this.searching = true;
      this.loaded = false;

      try {
        const { data } = await firebase.functions().httpsCallable('httpSearchUser')({ query: this.searchQuery.trim() });

        if (data && data.uid) {
          this.$router.push(`/user/${data.uid}`);
        } else {
          this.$toasted.error('User not found');
        }
      } catch (e) {
        this.$toasted.error('Error searching for user');
      }

      this.searching = false;
      this.loaded = true;
    },

    async handleCreate() {
      if (!this.emailInput.trim()) {
        return;
      }

      const ok = window.confirm('Are you sure you want to create this user?');
      if (!ok) return;

      this.creating = true;

      try {
        const { data } = await firebase.functions().httpsCallable('httpCreateEmptyUser')({ email: this.emailInput });
        this.$toasted.success('User created successfully');
        this.$router.push(`/user/${data.uid}`);
      } catch (e) {
        this.$toasted.error('Error creating user');
      }

      this.creating = false;
    },

    async handleCreateFreeStripeAccount() {
      if (!this.freeAccountEmail.trim() || !this.freeAccountCoupon.trim()) {
        return;
      }

      const ok = window.confirm('Are you sure you want to create or update this free Stripe account? If the account already exists, its password will be reset.');
      if (!ok) return;

      this.creatingFreeAccount = true;

      try {
        const { data } = await firebase.functions().httpsCallable('httpCreateFreeStripeAccount')({
          email: this.freeAccountEmail.trim(),
          coupon: this.freeAccountCoupon.trim(),
        });

        if (data && data.emailSent) {
          this.$toasted.success(data.updated ? 'Existing account updated and email sent' : 'Free Stripe account created and email sent');
        } else {
          this.$toasted.error(data && data.updated ? 'Account updated, but the email failed to send' : 'Account created, but the email failed to send');
        }

        if (data && data.uid) {
          this.$router.push(`/user/${data.uid}`);
        }
      } catch (e) {
        this.$toasted.error((e && e.message) || 'Error creating free Stripe account');
      }

      this.creatingFreeAccount = false;
    },

    async refreshCert() {
      this.refreshingCert = true;

      try {
        const { data } = await firebase.functions().httpsCallable('httpRefreshCert')({
          cert: this.certInput,
          grader: this.graderInput,
        });

        if (data === true) {
          this.$toasted.success('Cert refreshed successfully');
        } else {
          this.$toasted.error('Error refreshing cert');
        }
      } catch (e) {
        this.$toasted.error('Error refreshing cert');
      }

      this.refreshingCert = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.admin-view {
  @include container;
  min-height: 100%;

  form {
    max-width: 1000px;
  }
}

.search-container,
.create-container {
  --text-input-margin-bottom: 0;
  margin-bottom: $gp * 2;
}

.cert-input,
.free-account-input {
  margin-top: $gp;
  --select-input-margin-bottom: 0;
  --text-input-margin-bottom: 0;
}
</style>
